import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import ProtectedRoute from "./components/ProtectedRoute";
import UserManagement from "./pages/UserManagement";

import Login from "./components/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Landing from "./pages/Landing";
import Channel from "./pages/Channel";
import GoToPage from "./pages/GoToPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useLocationPreload } from "./hooks/useLocationPreload";
import Header from "./components/Header";
import Profile from "./pages/Profile";

/* NOTIFICATION */
import NotificationToast from "./components/notifications/NotificationToast";
import { NotificationProvider } from "./contexts/NotificationContext";

/* CHAT ASSISTANT */
import ChatWidget from "./components/ChatWidget";
import ForcePasswordChange from "./pages/ForcePasswordChange";

/* MOBILE */
import RotateDeviceOverlay from "./components/RotateDeviceOverlay";

/* =========================
   LAYOUT
========================= */
function LayoutWithSidebar() {
  const location = useLocation();

  const isLoginPage =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/forgot-password";

  // From the context, so setting the password re-renders straight into the
  // panel rather than leaving the gate up until the next navigation.
  const { user } = useAuth();
  const mustSetPassword = !!user?.mustchangepassword;

  useEffect(() => {
    const storedUser = JSON.parse(
      localStorage.getItem("adm_panel_user") || "{}"
    );

    if (storedUser.darkmode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, []);

  useLocationPreload(isLoginPage || mustSetPassword ? null : user);

  /* =========================
     LOGIN PAGE (NO NOTIFICATIONS)
  ========================= */
  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
    );
  }

  /* =========================
     FORCED PASSWORD CHANGE
     Stands alone like the login page: no header, no sidebar, no assistant.
     A gate that still shows the panel around it is not much of a gate.
  ========================= */
  if (mustSetPassword) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <ForcePasswordChange />
            </ProtectedRoute>
          }
        />
      </Routes>
    );
  }

  /* =========================
     APP (WITH NOTIFICATIONS)
  ========================= */
  return (
    <NotificationProvider>
      <>
        <Header />
        <NotificationToast />
        <ChatWidget />
        <RotateDeviceOverlay />

        <div className="app-layout">
          <main className="app-container">
            <Routes>
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Landing />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/channel/:id"
                element={
                  <ProtectedRoute>
                    <Channel />
                  </ProtectedRoute>
                }
              />

              {/* Shareable link to a page with no channel in it, e.g.
                  /go/channelgrid — lands on the recipient's first channel
                  that has the page. */}
              <Route
                path="/go/:page"
                element={
                  <ProtectedRoute>
                    <GoToPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/usermanagement"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
      </>
    </NotificationProvider>
  );
}

/* =========================
   APP ROOT
========================= */
function App() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LdaYEssAAAAAPZGrfC4qaCuKsCPocfYX3rZM5d2">
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Router>
            <LayoutWithSidebar />
          </Router>
        </LocalizationProvider>
      </AuthProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;